import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Sparkles, Check, Brain, Home as HomeIcon } from "lucide-react";

export default function Quiz() {
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: q }, { data: s }] = await Promise.all([
          api.get("/quiz/questions"),
          api.get("/quiz/status"),
        ]);
        setQuestions(q.questions || []);
        setAnswers(s.answers || {});
        // resume from first unanswered
        const firstMissing = (q.questions || []).findIndex((qq) => !(s.answers || {})[qq.key]);
        setStep(firstMissing === -1 ? 0 : firstMissing);
        if (s.completed) setDone(true);
      } catch (e) {
        toast.error(formatError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="max-w-2xl mx-auto p-10 text-center text-[color:var(--umg-text-2)]">Loading quiz…</div>;

  const q = questions[step];
  const total = questions.length;
  const answeredCount = questions.filter((qq) => answers[qq.key]).length;
  const progressPct = total ? Math.round((answeredCount / total) * 100) : 0;

  const submitAll = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/quiz/submit", { answers });
      if (data.completed) {
        setDone(true);
        toast.success("Quiz complete! Better matches ahead ✨");
      } else {
        toast.success("Saved progress");
      }
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const pick = async (val) => {
    const next = { ...answers, [q.key]: val };
    setAnswers(next);
    // auto-advance after 250ms
    setTimeout(() => {
      if (step < total - 1) setStep(step + 1);
      else {
        // last question — submit
        (async () => {
          setSaving(true);
          try {
            const { data } = await api.post("/quiz/submit", { answers: next });
            if (data.completed) {
              setDone(true);
              toast.success("Quiz complete! Better matches ahead ✨");
            }
          } catch (e) { toast.error(formatError(e)); }
          finally { setSaving(false); }
        })();
      }
    }, 250);
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-14 text-center" data-testid="quiz-done">
        <div className="w-20 h-20 mx-auto rounded-full grid place-items-center bg-gradient-to-br from-[#7C3AED] to-[#DB2777] text-white shadow-lg">
          <Check className="w-10 h-10" strokeWidth={2.5} />
        </div>
        <h1 className="font-display text-4xl mt-6">You're all set!</h1>
        <p className="text-sm text-[color:var(--umg-text-2)] mt-3">
          Umaang will now show you personalised <b>Compatibility Score %</b> on every profile — based on lifestyle, values, and traditional criteria.
        </p>
        <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-md mx-auto">
          <Button data-testid="quiz-go-home" onClick={() => nav("/home")} className="btn-pill bg-gradient-to-r from-[#C85A53] to-[#DB2777] text-white h-11">
            <HomeIcon className="w-4 h-4 mr-1.5" /> Discover matches
          </Button>
          <Button
            data-testid="quiz-retake"
            variant="outline"
            onClick={() => { setDone(false); setStep(0); }}
            className="btn-pill h-11"
          >
            Retake quiz
          </Button>
        </div>
      </div>
    );
  }

  if (!q) return null;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="quiz-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl grid place-items-center bg-gradient-to-br from-[#EDE9FE] to-[#FCE7F3] text-[#7C3AED]">
          <Brain className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl">AI Compatibility Quiz</h1>
          <p className="text-xs text-[color:var(--umg-text-2)] mt-0.5">8 questions · 60 seconds · unlocks lifestyle-based matches</p>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="text-[color:var(--umg-text-2)]">Question {step + 1} of {total}</span>
          <span className="font-mono text-[#7C3AED]">{progressPct}%</span>
        </div>
        <div className="h-2 rounded-full overflow-hidden" style={{ background: "#EDE9FE" }}>
          <div
            data-testid="quiz-progress-bar"
            className="h-full bg-gradient-to-r from-[#7C3AED] to-[#DB2777] transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Question card */}
      <div className="p-6 sm:p-8 rounded-3xl border" style={{ borderColor: "var(--umg-border)", background: "var(--umg-surface)" }} data-testid={`quiz-q-${q.key}`}>
        <div className="text-[10px] uppercase tracking-widest text-[#7C3AED] font-semibold flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Question {step + 1}
        </div>
        <h2 className="font-display text-2xl sm:text-3xl mt-3 leading-snug">{q.prompt}</h2>
        <p className="text-xs text-[color:var(--umg-text-2)] mt-1">{q.prompt_en}</p>

        <div className="mt-6 space-y-2.5">
          {q.options.map((o) => {
            const active = answers[q.key] === o.value;
            return (
              <button
                key={o.value}
                data-testid={`quiz-opt-${q.key}-${o.value}`}
                onClick={() => pick(o.value)}
                disabled={saving}
                className={`w-full text-left px-4 py-3.5 rounded-2xl border-2 transition-all text-sm font-medium flex items-center justify-between gap-3
                  ${active
                    ? "border-[#7C3AED] bg-gradient-to-r from-[#F5F3FF] to-[#FCE7F3] shadow-sm"
                    : "border-[color:var(--umg-border)] bg-[color:var(--umg-bg)] hover:border-[#DDD6FE] hover:bg-[#FAFAFA]"
                  }`}
              >
                <span>{o.label}</span>
                {active && (
                  <span className="inline-flex w-6 h-6 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#DB2777] text-white items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Navigation */}
      <div className="mt-6 flex items-center justify-between gap-3">
        <Button
          data-testid="quiz-back"
          variant="ghost"
          disabled={step === 0}
          onClick={() => setStep(Math.max(0, step - 1))}
          className="btn-pill"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <span className="text-xs text-[color:var(--umg-text-2)]">{answeredCount}/{total} answered</span>
        {step < total - 1 ? (
          <Button
            data-testid="quiz-next"
            onClick={() => setStep(step + 1)}
            variant="outline"
            className="btn-pill"
          >
            Skip <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            data-testid="quiz-finish"
            onClick={submitAll}
            disabled={saving || answeredCount < total}
            className="btn-pill bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white"
          >
            {saving ? "Saving…" : "Finish"}
          </Button>
        )}
      </div>
    </div>
  );
}
